import { useEffect, useState } from 'react';

/**
 * Custom hook to detect and track the user's dark mode preference.
 * Uses the browser's `prefers-color-scheme` media query to detect system preference
 * and automatically updates when the user changes their system theme.
 *
 * @returns {boolean} `true` if dark mode is enabled, `false` otherwise
 */
export function useDarkMode(): boolean {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    // Update state when preference changes
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Fallback for older browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  return isDarkMode;
}

